import { useEffect, useState } from 'react'
import { LiveGameTechProgress } from './LiveGameTechProgress'
import { getPbgidIcon, usePbgidIcons } from '../../helpers/pbgidIcons'

// Keeps techs on screen for `displayDuration` seconds after they leave the queue.
// A tech that left the queue and shows up in technologiesResearched has finished, otherwise it was cancelled.
// Entries without an icon are hidden helper upgrades the game queues when a tech completes, so they are skipped.
export function LiveGameTechQueue({ techQueue, technologiesResearched, displayDuration = 5, color }) {
	const [entries, setEntries] = useState([])
	const pbgidIcons = usePbgidIcons()
	const durationMs = Math.max(Number(displayDuration) || 0, 0) * 1000

	useEffect(() => {
		const queue = (techQueue ?? []).filter((tech) => getPbgidIcon(pbgidIcons, parseInt(tech.pbgId)))
		const now = Date.now()
		setEntries((previous) => {
			const remaining = [...queue]
			const updated = []
			for (const entry of previous) {
				const index = remaining.findIndex((tech) => String(tech.pbgId) === String(entry.pbgId))
				if (index !== -1) {
					updated.push({ ...remaining[index], endedAt: null })
					remaining.splice(index, 1)
				} else if (entry.endedAt == null) {
					if (durationMs > 0) updated.push({ ...entry, endedAt: now })
				} else if (now - entry.endedAt < durationMs) {
					updated.push(entry)
				}
			}
			return [...updated, ...remaining.map((tech) => ({ ...tech, endedAt: null }))]
		})
	}, [techQueue, durationMs, pbgidIcons])

	// remove ended techs once their display time is up
	useEffect(() => {
		const ended = entries.filter((entry) => entry.endedAt != null)
		if (!ended.length) return
		const nextExpiry = Math.min(...ended.map((entry) => entry.endedAt + durationMs))
		const timeout = setTimeout(
			() => {
				const now = Date.now()
				setEntries((previous) => previous.filter((entry) => entry.endedAt == null || now - entry.endedAt < durationMs))
			},
			Math.max(nextExpiry - Date.now(), 0),
		)
		return () => clearTimeout(timeout)
	}, [entries, durationMs])

	const researched = Object.values(technologiesResearched ?? {}).map(String)

	return entries.map((tech) => {
		const status = tech.endedAt == null ? 'active' : researched.includes(String(tech.pbgId)) ? 'finished' : 'cancelled'
		return (
			<LiveGameTechProgress
				key={tech.pbgId}
				pbgId={tech.pbgId}
				progress={status === 'finished' ? 1 : tech.progress}
				secondsRemaining={tech.secondsRemaining}
				status={status}
				color={color}
			/>
		)
	})
}
